/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';

import { setListingAssets, setMasterAssets } from './helpers/entityAdapter';
import { fetchAssets } from './fetchAssets';
import { fetchListingAssets } from './fetchListingAssets';
import {
  PRODUCT_VIEW_LAYER,
  isDraftOrderLayer,
} from '../../components/AssetTargetsDialog/helpers/assetProductLinks';

const applyOptimisticGalleryReorder = (assets, productId, assetId, order, layer) => {
  if (!Array.isArray(assets)) return assets;

  const currentIndex = assets.findIndex((asset) => String(asset._id) === String(assetId));
  if (currentIndex === -1) return assets;

  const clampedIndex = Math.max(0, Math.min(order, assets.length - 1));
  if (currentIndex === clampedIndex) return assets;

  const isDraft = isDraftOrderLayer(layer);
  const matchesLayer = (entry) => (
    String(entry.productId) === String(productId)
    && (isDraft ? entry.isDraft === true : entry.isDraft !== true)
  );

  const nextAssets = [...assets];
  const [moved] = nextAssets.splice(currentIndex, 1);
  nextAssets.splice(clampedIndex, 0, moved);

  return nextAssets.map((asset, idx) => {
    const customOrder = Array.isArray(asset.customOrder) ? [...asset.customOrder] : [];
    const entryIndex = customOrder.findIndex(matchesLayer);
    const nextEntry = isDraft
      ? { productId, isDraft: true, index: idx }
      : { productId, index: idx };

    if (entryIndex >= 0) {
      customOrder[entryIndex] = { ...customOrder[entryIndex], ...nextEntry };
    } else {
      customOrder.push(nextEntry);
    }

    return { ...asset, customOrder };
  });
};

const updateOptimisticGalleryOrder = (state, { productId, assetId, order, viewContext }) => {
  if (!productId || !assetId || !Number.isFinite(order)) return;
  const masterProductId = viewContext?.masterProductId || productId;

  if (
    viewContext?.listingProductId
    && viewContext?.channelId
    && viewContext?.layer === PRODUCT_VIEW_LAYER.STAGING
  ) {
    const listingAssets = state.entities?.assets?.listingAssets?.[viewContext.listingProductId];

    if (listingAssets) {
      const reorderedAssets = applyOptimisticGalleryReorder(
        listingAssets,
        productId,
        assetId,
        order,
        viewContext.layer,
      );
      setListingAssets(state, viewContext.listingProductId, reorderedAssets);
    }
    return;
  }

  const product = state.items.find((item) => item._id === masterProductId);
  if (!product?.assets) return;

  product.assets = applyOptimisticGalleryReorder(product.assets, productId, assetId, order, viewContext?.layer);
  setMasterAssets(state, masterProductId, product.assets);
};

export const changeOrder = createAsyncThunk(
  'products/changeOrder',
  async ({ assetId, productId, order, viewContext }, {
    dispatch,
    extra: { Logger, Toast, UiBlocker, sdk },
  }) => {
    try {
      UiBlocker.block('Reordering product media…');
      await sdk.assets.changeImageOrder(assetId, productId, order);
      const targetIndex = Number.isFinite(order) ? order : 0;
      Toast('Product media order updated successfully');

      if (
        viewContext?.listingProductId
        && viewContext?.channelId
        && viewContext?.layer === PRODUCT_VIEW_LAYER.STAGING
      ) {
        await dispatch(fetchListingAssets({
          masterProductId: viewContext.masterProductId,
          channelId: viewContext.channelId,
          listingProductId: viewContext.listingProductId,
        }));
      } else {
        await dispatch(fetchAssets({
          productId: viewContext?.masterProductId || productId,
        }));
      }

      return {
        productId,
        assetId,
        order: targetIndex,
        viewContext,
      };
    } catch (error) {
      Logger.error(new Error('Product assets reorder failed'), { error });
      Toast('Failed to update product media order');
      throw error;
    } finally {
      UiBlocker.unblock();
    }
  },
);

export const reducer = (builder) => {
  builder.addCase(
    changeOrder.pending,
    (state, { meta }) => {
      state.isChangingOrder = true;
      state.search.isLoading = true;
      updateOptimisticGalleryOrder(state, meta.arg);
    },
  ).addCase(
    changeOrder.fulfilled,
    (state, { payload }) => {
      state.isChangingOrder = false;
      state.search.isLoading = false;

      try {
        updateOptimisticGalleryOrder(state, payload || {});
      } catch (e) {
        // ignore reducer-time reorder errors
      }
    },
  ).addCase(
    changeOrder.rejected,
    (state, { error }) => {
      state.isChangingOrder = false;
      state.search.isLoading = false;
      state.error = error;
    },
  );
};
